import { dismissFloatingFrameSource } from "./floating-frame-focus-dismissal";
import { getFloatingFrameDisplayArea } from "./floating-frame-displays";
import { clampFrameHeaderToSurface } from "@/lib/frame-window-gestures";
import { create } from "zustand";
import { emit, listen } from "@tauri-apps/api/event";
import type { FrameData } from "@/lib/frame-data";

import type {
  FocusWindowBox,
  FocusWindowMode,
} from "@/lib/focus-window-layout";
import { isTauri } from "@/lib/is-tauri";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

export interface FloatingFrame {
  frameId: string;
  /** The frame the window carries between displays, kept current by local
   *  patches. The window body prefers this display's full frames-store copy. */
  frame: FrameData;
  projectId: string;
  spaceId: string;
  box: FocusWindowBox;
  normalBox: FocusWindowBox;
  displayId: number;
  sourceDisplayId: number;
  dragging: boolean;
  order?: number;
  mode: FocusWindowMode;
  /** A card Denker showed from another of the user's projects: on screen
   *  whatever project is viewed, never on the viewed wall. */
  otherProject?: true;
  snapPreview?: {
    mode: Exclude<FocusWindowMode, "normal">;
    box: FocusWindowBox;
  };
}

interface FrameUpdate {
  frameId: string;
  revision: number;
  frame:
    | (Omit<FloatingFrame, "frame"> & { frame?: FloatingFrame["frame"] })
    | null;
  authScope: DesktopChatAuthScope | null;
}
const EVENT = "denker://floating-frame-update";
const REQUEST = "denker://floating-frame-request";
const versions = new Map<string, FrameUpdate>();
const MISSING_REQUEST_INTERVAL_MS = 250;
let lastMissingRequestAt = -Infinity;
export const useFloatingFrameStore = create<{
  frames: Record<string, FloatingFrame>;
}>(() => ({ frames: {} }));

export function applyFloatingFrameUpdate(update: FrameUpdate): boolean {
  if (!isCurrentDesktopChatAuthScope(update.authScope)) return false;
  if (!Number.isFinite(update.revision)) return false;
  if ((versions.get(update.frameId)?.revision ?? -1) >= update.revision)
    return false;
  const carried =
    update.frame?.frame ??
    useFloatingFrameStore.getState().frames[update.frameId]?.frame;
  if (update.frame && !carried) {
    // Geometry arrives at 30 Hz while the creation is missing: ask once.
    const now = Date.now();
    if (
      isTauri() &&
      now - lastMissingRequestAt >= MISSING_REQUEST_INTERVAL_MS
    ) {
      lastMissingRequestAt = now;
      void emit(REQUEST).catch(() => {});
    }
    return false;
  }
  const frame =
    update.frame && carried ? { ...update.frame, frame: carried } : null;
  versions.set(update.frameId, { ...update, frame });
  if (!update.frame) dismissFloatingFrameSource(update.frameId);
  useFloatingFrameStore.setState((state) => {
    const frames = { ...state.frames };
    if (frame) frames[update.frameId] = frame;
    else delete frames[update.frameId];
    return { frames };
  });
  return true;
}

export function putFloatingFrame(
  frame: FloatingFrame | null,
  frameId = frame?.frameId,
) {
  if (!frameId) return;
  const previous = useFloatingFrameStore.getState().frames[frameId];
  const update: FrameUpdate = {
    frameId,
    frame,
    authScope: getDesktopChatAuthScope(),
    revision: Math.max(
      Date.now() * 1000,
      (versions.get(frameId)?.revision ?? 0) + 1,
    ),
  };
  if (!applyFloatingFrameUpdate(update)) return;
  if (isTauri()) {
    // Content travels once at creation; 30 Hz drag messages contain geometry only.
    const wireUpdate =
      frame && previous?.frame === frame.frame
        ? { ...update, frame: { ...frame, frame: undefined } }
        : update;
    void emit(EVENT, wireUpdate).catch(() => {});
  }
}

export function nextFloatingFrameOrder(): number {
  return (
    1 +
    Math.max(
      0,
      ...Object.values(useFloatingFrameStore.getState().frames).map(
        (frame) => frame.order ?? 0,
      ),
    )
  );
}

/** Brings a window to the front; one already alone on top sends nothing. */
export function raiseFloatingFrame(frameId: string) {
  const frames = useFloatingFrameStore.getState().frames;
  const frame = frames[frameId];
  if (!frame) return;
  const order = frame.order ?? 0;
  const onTop = Object.values(frames).every(
    (other) => other.frameId === frameId || (other.order ?? 0) < order,
  );
  if (!onTop) putFloatingFrame({ ...frame, order: nextFloatingFrameOrder() });
}

export function closeFloatingFrame(frameId: string) {
  putFloatingFrame(null, frameId);
}

export function updateFloatingFrameBox(frameId: string, box: FocusWindowBox) {
  const frame = useFloatingFrameStore.getState().frames[frameId];
  if (!frame) return;
  const area = getFloatingFrameDisplayArea(frame.displayId);
  const reachableBox = area ? clampFrameHeaderToSurface(box, area) : box;
  putFloatingFrame({
    ...frame,
    box: reachableBox,
    normalBox: reachableBox,
    mode: "normal",
  });
}

/** Runs on every display, including mirrors that have not mounted the app. */
export function attachFloatingFrames(): () => void {
  if (!isTauri()) return () => {};
  let disposed = false;
  const unlisteners: Array<() => void> = [];
  const add = (unlisten: () => void) => {
    if (disposed) safeTauriUnlisten(unlisten);
    else unlisteners.push(unlisten);
  };
  const ready = Promise.all([
    listen<FrameUpdate>(EVENT, ({ payload }) =>
      applyFloatingFrameUpdate(payload),
    ).then(add),
    listen(REQUEST, () => {
      for (const update of versions.values())
        void emit(EVENT, update).catch(() => {});
    }).then(add),
  ]);
  const reset = subscribeDesktopChatAuthScope(() => {
    versions.clear();
    useFloatingFrameStore.setState({ frames: {} });
    void emit(REQUEST).catch(() => {});
  });
  void ready
    .then(() => {
      if (!disposed) return emit(REQUEST);
    })
    .catch(() => {});
  return () => {
    disposed = true;
    reset();
    unlisteners.forEach(safeTauriUnlisten);
  };
}
